import React, { useEffect, useState } from "react";
import { Star, Send } from "lucide-react";
import { toast } from "sonner";
import apiClient, { formatApiErrorDetail } from "@/utils/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

function StarRow({ rating, onSelect, size = "w-5 h-5" }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onSelect?.(n)}
          className={onSelect ? "cursor-pointer" : "cursor-default"}
          aria-label={`${n} stars`}
        >
          <Star
            className={`${size} ${
              n <= rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-stone-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function ReviewsSection({ productId }) {
  const { isSignedIn, signInWithGoogle, customer } = useCustomerAuth();
  const [data, setData] = useState({ reviews: [], count: 0, average_rating: 0 });
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await apiClient.get(`/reviews/${productId}`);
      setData(d);
    } catch (e) {
      // silent — reviews are additive
    }
  };

  useEffect(() => {
    if (productId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isSignedIn) {
      signInWithGoogle();
      return;
    }
    if (text.trim().length < 3) {
      toast.error("Please write a longer review.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/reviews", {
        product_id: productId,
        rating,
        review_text: text.trim(),
        name: customer?.name || "",
      });
      setSubmitted(true);
      setText("");
      setRating(5);
      toast.success("Thanks! Your review is pending approval.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="product-reviews-section"
      className="max-w-5xl mx-auto bg-white rounded-3xl border border-[#E5E0D8] p-6 sm:p-8 space-y-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold text-stone-900">Customer Reviews</h2>
          <div className="flex items-center gap-2 pt-1">
            <StarRow rating={Math.round(data.average_rating)} />
            <span data-testid="reviews-average" className="text-xs text-stone-600">
              {data.average_rating || 0} / 5 · {data.count} review{data.count === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </div>

      {/* Approved reviews */}
      {data.reviews.length === 0 ? (
        <p data-testid="reviews-empty" className="text-xs italic text-stone-500">
          No approved reviews yet. Be the first — approved reviews appear here after admin approval.
        </p>
      ) : (
        <ul data-testid="reviews-list" className="space-y-3">
          {data.reviews.map((r) => (
            <li
              key={r.id}
              data-testid={`review-${r.id}`}
              className="bg-[#FAF8F5] border border-stone-200 rounded-2xl p-4 space-y-1"
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-stone-900">{r.name}</span>
                  <StarRow rating={r.rating} size="w-3.5 h-3.5" />
                </div>
                <span className="text-[10px] text-stone-400">
                  {new Date(r.created_at).toLocaleDateString()}
                </span>
              </div>
              <p className="text-xs text-stone-700 whitespace-pre-wrap">{r.review_text}</p>
            </li>
          ))}
        </ul>
      )}

      {/* Submit form */}
      <div className="pt-4 border-t border-[#E5E0D8] space-y-3">
        <h3 className="font-heading font-bold text-sm text-stone-900">Write a Review</h3>
        {!isSignedIn ? (
          <div className="space-y-2">
            <p className="text-xs text-stone-500">
              Please sign in with your customer account to submit a review.
            </p>
            <button
              type="button"
              onClick={signInWithGoogle}
              data-testid="reviews-signin-btn"
              className="px-4 py-2 bg-white border border-stone-300 hover:border-[#D4AF37] rounded-xl font-semibold text-xs text-stone-800 flex items-center gap-2"
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt=""
                className="w-4 h-4"
              />
              <span>Continue with Google to Review</span>
            </button>
          </div>
        ) : submitted ? (
          <p data-testid="reviews-submitted" className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            Thanks! Your review has been submitted and is pending admin approval.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase text-stone-600">Your rating:</span>
              <StarRow rating={rating} onSelect={setRating} />
            </div>
            <textarea
              rows={3}
              maxLength={2000}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Share your experience with this lace set..."
              data-testid="review-text-input"
              className="w-full px-3 py-2 text-xs bg-[#FAF8F5] border border-stone-300 rounded-lg resize-none"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="review-submit-btn"
              className="px-4 py-2 bg-[#1C1917] hover:bg-[#D4AF37] hover:text-[#1C1917] text-white text-xs font-bold rounded-xl transition flex items-center gap-2 disabled:opacity-60"
            >
              <Send className="w-3.5 h-3.5" />
              <span>{submitting ? "Submitting…" : "Submit Review"}</span>
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
